import type { Theme } from './theme';

type Rgb = [number, number, number];

const parseHex = (hex: string): Rgb => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];

const toHex = (rgb: Rgb) =>
    `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** `amount` of `b` mixed into `a`, in sRGB (what CSS color-mix does by default). */
export function mix(a: string, b: string, amount: number): string {
    const [x, y] = [parseHex(a), parseHex(b)];
    return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * amount) as Rgb);
}

function relativeLuminance(hex: string): number {
    const [r, g, b] = parseHex(hex).map((c) => {
        const s = c / 255;
        return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    }) as Rgb;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2 contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
    const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
        number,
        number,
    ];
    return (hi + 0.05) / (lo + 0.05);
}

export const AA_TEXT = 4.5;
/** WCAG 1.4.11: buttons and other UI shapes against their surroundings. */
export const AA_UI = 3;

const INK = '#141414';
const PAPER = '#FFFFFF';

/**
 * Near-black or white, whichever reads better on `background`. Falls back to
 * pure black for the mid-tones where near-black misses AA; black or white
 * always reaches at least 4.58:1.
 */
export function readableOn(background: string): string {
    const ink = contrastRatio(INK, background);
    const paper = contrastRatio(PAPER, background);
    if (Math.max(ink, paper) >= AA_TEXT) return ink >= paper ? INK : PAPER;
    return contrastRatio('#000000', background) >= paper ? '#000000' : PAPER;
}

/** The first of `candidates` that reaches `ratio` on `background`, else `fallback`. */
const firstReadable = (
    candidates: string[],
    background: string,
    fallback: string,
    ratio = AA_TEXT,
) => candidates.find((c) => contrastRatio(c, background) >= ratio) ?? fallback;

/** Concrete colors a template renders with. Every text/background pair here meets AA. */
export type ThemeTokens = {
    bg: string;
    fg: string;
    /** Secondary text. As light as it can be while still meeting AA on bg. */
    muted: string;
    /** Hairlines and dividers; decorative, so no contrast requirement. */
    border: string;
    primary: string;
    onPrimary: string;
    /** Outline buttons and links: the brand color when it is readable on bg, else fg. */
    primaryText: string;
    accent: string;
    onAccent: string;
    /** A light wash of the accent, for countdown and progress backgrounds. */
    tint: string;
    onTint: string;
};

export function deriveTokens(theme: Theme): ThemeTokens {
    const { background: bg, primary, accent } = theme;
    const fg = readableOn(bg);
    const tint = mix(bg, accent, 0.16);
    return {
        bg,
        fg,
        muted: firstReadable(
            [0.4, 0.35, 0.3, 0.25, 0.2, 0.15, 0.1].map((amount) => mix(fg, bg, amount)),
            bg,
            fg,
        ),
        border: mix(bg, fg, 0.14),
        primary,
        onPrimary: readableOn(primary),
        primaryText: firstReadable([primary], bg, fg),
        accent,
        onAccent: readableOn(accent),
        tint,
        onTint: firstReadable([fg], tint, readableOn(tint)),
    };
}

export type BrandWarning = { color: 'primary'; ratio: number };

/**
 * Brand colors that are hard to see against the background. Text is always
 * readable (it is derived), but a filled button that barely differs from the
 * page is easy to miss. The accent only ever sits behind derived text (the
 * label tag, countdown), so a soft accent is fine and isn't flagged.
 */
export function brandWarnings(theme: Theme): BrandWarning[] {
    const ratio = contrastRatio(theme.primary, theme.background);
    return ratio < AA_UI ? [{ color: 'primary', ratio }] : [];
}
